import { useEffect, useRef, useState } from "react";

/**
 * Matches the previous `calc(100% + spacing-1)` gap now that the menu is
 * positioned via JS (portalled to document.body so parent `overflow: hidden`
 * containers, e.g. card sections, can't clip it).
 */
export const MENU_GAP = 4;

/**
 * Finds the next non-disabled option index, wrapping around the list.
 * @param {Array<{disabled?: boolean}>} options Options to search.
 * @param {number} start Index to start searching from (exclusive).
 * @param {number} direction 1 to search forward, -1 to search backward.
 * @returns {number} The next enabled index, or -1 if every option is disabled/there are none.
 */
export function getNextEnabledIndex(options, start, direction) {
    if (!options.length) {
        return -1;
    }

    let index = start;

    for (let count = 0; count < options.length; count += 1) {
        index = (index + direction + options.length) % options.length;

        if (!options[index] || !options[index].disabled) {
            return index;
        }
    }

    return -1;
}

/**
 * Computes the next highlighted index for an ArrowUp/ArrowDown key press - starting
 * from the first/last enabled option when nothing is highlighted yet (e.g. the menu
 * was just opened by this same key press).
 * @param {Array<{disabled?: boolean}>} options Options to search.
 * @param {number} currentIndex Current highlighted index, or -1 if none.
 * @param {"ArrowDown" | "ArrowUp"} key The arrow key that was pressed.
 * @returns {number} The next enabled index, or -1 if every option is disabled/there are none.
 */
export function getNextHighlightedIndexOnArrowKey(options, currentIndex, key) {
    const direction = key === "ArrowDown" ? 1 : -1;
    const start = currentIndex < 0 ? (key === "ArrowDown" ? -1 : 0) : currentIndex;

    return getNextEnabledIndex(options, start, direction);
}

/**
 * Tracks a portalled dropdown menu's fixed position against its trigger, recalculating
 * on resize/scroll while open.
 * @param {React.RefObject} containerRef Ref to the trigger element the menu is anchored to.
 * @param {boolean} isOpen Whether the menu is currently shown.
 * @param {{dropUp?: boolean, align?: "left"|"right", autoFlip?: boolean, listRef?: React.RefObject}} [options]
 *   `dropUp: true` always anchors the menu above the trigger instead of below. `align: "right"`
 *   anchors the menu's right edge to the trigger's right edge (opening leftward) instead of the
 *   default left-to-left. `autoFlip: true` (paired with `listRef`, the portalled menu element)
 *   additionally flips the menu upward whenever there isn't room below the trigger for its actual
 *   rendered height but there's more room above than below — e.g. a "Rows per page" select near
 *   the bottom of a scrollable table.
 * @returns {{top: number, left: number|undefined, right: number|undefined, width: number, dropUp: boolean}}
 */
export function useDropdownPosition(containerRef, isOpen, { dropUp = false, align = "left", autoFlip = false, listRef } = {}) {
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, right: undefined, width: 0, dropUp });

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const updateMenuPosition = () => {
            if (!containerRef.current) {
                return;
            }

            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const menuHeight = autoFlip && listRef?.current ? listRef.current.offsetHeight : 0;
            const resolvedDropUp = dropUp || (autoFlip && spaceBelow < menuHeight + MENU_GAP && spaceAbove > spaceBelow);
            const top = resolvedDropUp ? rect.top - MENU_GAP : rect.bottom + MENU_GAP;

            setMenuPosition(
                align === "right"
                    ? { top, left: undefined, right: window.innerWidth - rect.right, width: rect.width, dropUp: resolvedDropUp }
                    : { top, left: rect.left, right: undefined, width: rect.width, dropUp: resolvedDropUp }
            );
        };

        updateMenuPosition();

        window.addEventListener("resize", updateMenuPosition);
        window.addEventListener("scroll", updateMenuPosition, true);

        return () => {
            window.removeEventListener("resize", updateMenuPosition);
            window.removeEventListener("scroll", updateMenuPosition, true);
        };
    }, [isOpen, dropUp, align, autoFlip, listRef, containerRef]);

    return menuPosition;
}

/**
 * Closes an open dropdown when a mousedown lands outside both its trigger and its (portalled) menu.
 * @param {React.RefObject} containerRef Ref to the trigger element.
 * @param {React.RefObject} listRef Ref to the portalled menu element.
 * @param {boolean} isOpen Whether the menu is currently shown.
 * @param {Function} onOutsideClick Called (no args) when a qualifying outside click happens.
 */
export function useOutsideClick(containerRef, listRef, isOpen, onOutsideClick) {
    const onOutsideClickRef = useRef(onOutsideClick);
    onOutsideClickRef.current = onOutsideClick;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isOpen) {
                return;
            }

            const clickedTrigger = containerRef.current && containerRef.current.contains(event.target);
            const clickedMenu = listRef.current && listRef.current.contains(event.target);

            if (!clickedTrigger && !clickedMenu) {
                onOutsideClickRef.current();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);
}

/**
 * Scrolls the highlighted (keyboard-navigated) option into view within the open menu.
 * @param {React.RefObject} listRef Ref to the portalled menu element.
 * @param {boolean} isOpen Whether the menu is currently shown.
 * @param {number} highlightedIndex Index of the highlighted option, or -1 if none.
 */
export function useScrollHighlightedIntoView(listRef, isOpen, highlightedIndex) {
    useEffect(() => {
        if (!isOpen || highlightedIndex < 0 || !listRef.current) {
            return;
        }

        const highlightedItem = listRef.current.children[highlightedIndex];

        if (highlightedItem && highlightedItem.scrollIntoView) {
            highlightedItem.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex, isOpen]);
}
